import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHasModule } from '@/auth/auth-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  SCOPE_LABEL,
  SCOPE_LABEL_KEY,
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  cardCategoryWords,
  describeCardCategories,
  isImportedPromotion,
  kindLabel,
  usePromotions,
  type PromotionStatus,
} from '@/hooks/use-promotions';
import { isLiveButNotPriced, useLoyaltyCampaignImport } from '@/hooks/use-loyalty-campaign-import';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

const FILTERS: { value: 'all' | 'active' | PromotionStatus; key: string; label: string }[] = [
  { value: 'active', key: 'promotions.filter.active', label: 'Active' },
  { value: 'all', key: 'promotions.filter.all', label: 'All' },
  { value: 'paused', key: 'promotions.filter.paused', label: 'Paused' },
  { value: 'archived', key: 'promotions.filter.archived', label: 'Archived' },
];

function statusVariant(s: PromotionStatus) {
  switch (s) {
    case 'active':
      return 'success' as const;
    case 'paused':
      return 'outline' as const;
    case 'archived':
    default:
      return 'destructive' as const;
  }
}

export function PromotionsListPage() {
  const t = useT('admin_ui');
  const hasLoyalty = useHasModule('loyalty');
  const [filter, setFilter] = useState<'all' | 'active' | PromotionStatus>('active');
  const { data, isLoading } = usePromotions({
    pageSize: 100,
    status: filter === 'all' ? undefined : filter === 'active' ? ['active'] : [filter],
  });
  const items = data?.items ?? [];
  // RL-419: a campaign the importer decided against has no row below, so
  // someone looking for it here must be told it exists and why it is absent.
  const { data: importStatus } = useLoyaltyCampaignImport({ enabled: hasLoyalty });
  const notPriced = hasLoyalty
    ? (importStatus?.report?.campaigns ?? []).filter(isLiveButNotPriced).length
    : 0;

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('promotions.list.title', 'Promotions')}</h1>
          {/* Rows here are the authored rule set (TRS §19). Terminals pull it
              through sync and evaluate it with the same rules as the BOS
              evaluator — mechanism stays in this comment, not in the copy (RL-331). */}
          <p className="text-sm text-muted-foreground">
            {t(
              'promotions.list.subtitle',
              'Discounts and promotions for your stores. Terminals receive changes automatically and apply them at the till exactly as they are set here.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* YUKI-248: a skipped campaign has no row in this list, so the only
              way to learn it exists is to go and look. */}
          {hasLoyalty && (
            <Button asChild variant="outline">
              <Link to="/promotions/campaign-import">
                {t('promotions.import.link', 'Loyalty import')}
              </Link>
            </Button>
          )}
          <Button asChild>
            <Link to="/promotions/new">{t('promotions.new.title', 'New promotion')}</Link>
          </Button>
        </div>
      </header>

      {notPriced > 0 && (
        <p
          role="status"
          className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm"
        >
          <strong>{notPriced}</strong>{' '}
          {t(
            'promotions.list.importNotPriced',
            'campaign(s) active in re:form Loyalty are not priced by the till and have no row here.',
          )}{' '}
          <Link className="text-primary underline" to="/promotions/campaign-import?problems=1">
            {t('promotions.list.importNotPricedLink', 'See why')}
          </Link>
        </p>
      )}

      <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {FILTERS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'rounded-sm px-3 py-1.5 transition-colors',
              filter === opt.value
                ? 'bg-secondary font-medium text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary/60',
            )}
            onClick={() => setFilter(opt.value)}
          >
            {t(opt.key, opt.label)}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('promotions.list.emptyLead', 'No promotions match this filter. Click')}{' '}
            <strong>{t('promotions.new.title', 'New promotion')}</strong>{' '}
            {t('promotions.list.emptyTail', 'to author one.')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('promotions.field.code', 'Code')}</TH>
                <TH>{t('promotions.field.displayName', 'Display name')}</TH>
                <TH>{t('promotions.field.kind', 'Kind')}</TH>
                <TH>{t('promotions.field.scope', 'Scope')}</TH>
                <TH>{t('promotions.field.appliesTo', 'Applies to')}</TH>
                <TH className="text-right">{t('promotions.field.priority', 'Priority')}</TH>
                <TH>{t('promotions.field.status', 'Status')}</TH>
                <TH className="text-right">{t('promotions.field.actions', 'Actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((p) => (
                <TR key={p.id}>
                  <TD className="font-mono text-xs">{p.code}</TD>
                  <TD>
                    {p.displayName}
                    {isImportedPromotion(p) && (
                      <Badge variant="outline" className="ml-2">
                        {p.source === 'connector'
                          ? t('promotions.list.storeSystemBadge', 'Store system')
                          : t('promotions.list.loyaltyBadge', 'Loyalty')}
                      </Badge>
                    )}
                  </TD>
                  <TD>{kindLabel(p.kind, t)}</TD>
                  <TD>{t(SCOPE_LABEL_KEY[p.scope], SCOPE_LABEL[p.scope])}</TD>
                  <TD className="text-xs">
                    {describeCardCategories(p.cardCategories, cardCategoryWords(t))}
                  </TD>
                  <TD className="text-right font-mono text-xs">{p.priority}</TD>
                  <TD>
                    <Badge variant={statusVariant(p.status)}>
                      {t(STATUS_LABEL_KEY[p.status], STATUS_LABEL[p.status])}
                    </Badge>
                  </TD>
                  <TD className="text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/promotions/${p.id}`}>{t('promotions.list.open', 'Open')}</Link>
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}
